import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const QuoteForm = ({ compact = false }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    moveDate: '',
    pickupCity: '',
    dropoffCity: '',
    homeSize: '',
    notes: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, homeSize: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.phone || !formData.email || !formData.moveDate || 
        !formData.pickupCity || !formData.dropoffCity || !formData.homeSize) {
      alert('Please fill in all required fields.');
      return;
    }

    // Create mailto link with prefilled details
    const subject = encodeURIComponent('Moving Quote Request - BDC Movers');
    const body = encodeURIComponent(`
Hello BDC Movers,

I would like to request a quote for my move:

Full Name: ${formData.fullName}
Phone: ${formData.phone}
Email: ${formData.email}
Move Date: ${formData.moveDate}
Pickup City: ${formData.pickupCity}
Drop-off City: ${formData.dropoffCity}
Home Size: ${formData.homeSize}
Additional Notes: ${formData.notes || 'None'}

Please contact me at your earliest convenience.

Thank you!
    `);

    window.location.href = `mailto:contact@blackbrosdelivery.ca?subject=${subject}&body=${body}`;
    
    // Show success message
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    
    // Reset form
    setFormData({
      fullName: '',
      phone: '',
      email: '',
      moveDate: '',
      pickupCity: '',
      dropoffCity: '',
      homeSize: '',
      notes: ''
    });
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-green-800 mb-2">
          Thanks! We'll contact you shortly.
        </h3>
        <p className="text-sm text-green-700">
          Your email client should have opened with your quote details.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="fullName">Full Name *</Label>
          <Input
            id="fullName"
            name="fullName"
            type="text"
            value={formData.fullName}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="moveDate">Move Date *</Label>
          <Input
            id="moveDate"
            name="moveDate"
            type="date"
            value={formData.moveDate}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="pickupCity">Pickup City *</Label>
          <Input
            id="pickupCity"
            name="pickupCity"
            type="text"
            value={formData.pickupCity}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="dropoffCity">Drop-off City *</Label>
          <Input
            id="dropoffCity"
            name="dropoffCity"
            type="text"
            value={formData.dropoffCity}
            onChange={handleChange}
            required
            className="mt-1"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="homeSize">Home Size *</Label>
        <Select value={formData.homeSize} onValueChange={handleSelectChange} required>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select home size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Studio">Studio</SelectItem>
            <SelectItem value="1BR">1 Bedroom</SelectItem>
            <SelectItem value="2BR">2 Bedrooms</SelectItem>
            <SelectItem value="3BR">3 Bedrooms</SelectItem>
            <SelectItem value="House">House</SelectItem>
            <SelectItem value="Office">Office</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="notes">Additional Notes (Optional)</Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          className="mt-1"
        />
      </div>

      <Button 
        type="submit" 
        className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-full transition-all duration-300 hover:scale-105"
      >
        Request My Quote
      </Button>
    </form>
  );
};

export default QuoteForm;
